import { InfiniteData, QueryClient } from '@tanstack/react-query';

import { CONTACT_IMPORT_QUERY_KEYS } from './queries';
import type {
  PatchSessionRowsRes,
  RowPatch,
  SessionRow,
  SessionRowsPage,
  SessionSummary,
} from './types';

/** Merges row patches (+ conflict overrides) directly into every cached page for this session —
 * both the single-page and infinite-query shapes — instead of invalidating and refetching. */
export const applyRowPatchesToCache = (
  queryClient: QueryClient,
  sessionId: string,
  patches: RowPatch[],
  response: PatchSessionRowsRes,
) => {
  const conflictRowById = new Map(
    response.conflicts
      .filter((conflict) => conflict.row)
      .map((conflict) => [conflict.rowId, conflict.row as SessionRow]),
  );
  const patchByRowId = new Map(patches.map((patch) => [patch.rowId, patch]));
  const appliedIds = new Set(response.applied);

  const patchRow = (row: SessionRow): SessionRow => {
    const conflictRow = conflictRowById.get(row.rowId);
    if (conflictRow) return conflictRow;

    if (!appliedIds.has(row.rowId)) return row;
    const patch = patchByRowId.get(row.rowId);
    if (!patch) return row;

    return {
      ...row,
      checked: patch.checked ?? row.checked,
      decision: patch.decision !== undefined ? patch.decision : row.decision,
      chosenContactId: patch.chosenContactId ?? row.chosenContactId,
      effectiveDecision: patch.decision
        ? { decision: patch.decision, targetContactId: patch.chosenContactId ?? null }
        : row.effectiveDecision,
    };
  };

  const patchPage = (page: SessionRowsPage): SessionRowsPage => ({
    ...page,
    rows: page.rows.map(patchRow),
  });

  queryClient.setQueriesData<SessionRowsPage>(
    { queryKey: [CONTACT_IMPORT_QUERY_KEYS.sessionRows, sessionId] },
    (page) => (page ? patchPage(page) : page),
  );

  queryClient.setQueriesData<InfiniteData<SessionRowsPage>>(
    { queryKey: [CONTACT_IMPORT_QUERY_KEYS.sessionRowsFeed, sessionId] },
    (data) => (data ? { ...data, pages: data.pages.map(patchPage) } : data),
  );

  setSessionFooterCache(queryClient, sessionId, response.footer);
};

export const setSessionFooterCache = (
  queryClient: QueryClient,
  sessionId: string,
  footer: PatchSessionRowsRes['footer'],
) => {
  queryClient.setQueryData<SessionSummary>(
    [CONTACT_IMPORT_QUERY_KEYS.sessionById, sessionId],
    (summary) => (summary ? { ...summary, footer } : summary),
  );
};
